import getMailTipsViewStateMap from './getMailTipsViewStateMap';
import { logCollabSpaceInfo } from './logCollabSpaceInfo';
import logFreeBusyInfo from './logFreeBusyInfo';
import { updateCollabSpace } from './updateCollabSpace';
import logEventMetricProperties from '../utils/logEventMetricProperties';
import sendReinforcementLearningRewardsForTimeSuggestions from '../utils/sendReinforcementLearningRewardsForTimeSuggestions';
import { toJS } from 'mobx';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { isEventFromDifferentMailbox } from 'owa-calendar-collaborative-space';
import canNavigateAway from 'owa-calendar-compose-form-orchestration/lib/utils/canNavigateAway';
import generateEventDiff from 'owa-calendar-compose-form-orchestration/lib/utils/generateEventDiff';
import validateSend from 'owa-calendar-compose-form-orchestration/lib/utils/validateSend';
import getComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getComposeViewState';
import getFullComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getFullComposeViewState';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { FullComposeViewState } from 'owa-calendar-compose-types';
import { isEventAMeeting, isFTDFSeriesMaster } from 'owa-calendar-event-capabilities';
import { EVENT_COMPLETION_STATUS_SAVED } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { convertToServiceInboxReminders } from 'owa-email-reminders';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getDocLinks } from 'owa-link-data/lib/utils/getDocLinks';
import { logSendActionMailTipsDatapoint } from 'owa-mail-tips/lib/datapoints';
import { lazySaveTravelBookends } from 'owa-mobility-compose';
import { PopoutMessageType, postMessage } from 'owa-popout-v2/lib/utils/crossWindowMessenger';
import EventScope from 'owa-service/lib/contract/EventScope';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import {
    OwaDate,
    owaDate,
    addDays,
    startOfDay,
    getISOString,
    differenceInMinutes,
    startOfHour,
} from 'owa-datetime';
import {
    onDraftEventCreated,
    setCurrentSaveRequestTimeStamp,
    closeComposeForm,
    logComposeFormOpenedTimes,
    prepareEvent,
    updateCalendarEvents,
    setAwaitingServiceResponse,
} from 'owa-calendar-compose-form-actions';
import {
    newCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
} from 'owa-calendar-events-actions';
import {
    lazyShowOnlineMeetingFREDialog,
    lazyShowSpeedyMeetingsFREDialog,
} from 'owa-calendar-compose-form-utils';

export default async function saveEventInternal(perfDatapoint: PerformanceDatapoint): Promise<any> {
    const { metricsInfo, openedForm, uiState } = getComposeStore();
    let event = getComposeStore().event;
    let originalEvent = getComposeStore().originalEvent;

    if (shouldCloseComposeForm() && !(await canNavigateAway())) {
        // End the perf datapoint by invalidating it as no service request has been made
        perfDatapoint.invalidate();
        return;
    }

    if (isSending() && !(await validateSend())) {
        // End the perf datapoint by invalidating it as no service request has been made
        perfDatapoint.invalidate();
        return;
    }

    // Update the calendar event to set collaborative space from the collaborative space store
    // Check if event belongs to a group calendar, delegate calendar or connected account and if so then
    // avoid setting collaborative space for that event.
    if (isFeatureEnabled('cal-cmp-fluidCollaborativeSpace')) {
        const eventFromDifferentMailbox = isEventFromDifferentMailbox(event);
        if (!eventFromDifferentMailbox) {
            await updateCollabSpace(event, getComposeViewState().collabSpaceViewStateId);
        }
        await logCollabSpaceInfo(
            event,
            eventFromDifferentMailbox,
            perfDatapoint,
            getComposeViewState().collabSpaceViewStateId
        );
    }

    prepareEvent();

    // Pull event and originalEvent from the store again as they may have been updated
    // by addins on send or by prepareEvent
    event = getComposeStore().event;
    originalEvent = getComposeStore().originalEvent;

    let diffEvent: CalendarEvent = generateEventDiff(event, originalEvent);

    const { quickComposeEntrySource, fullComposeEntrySource } = metricsInfo;

    let quickComposeDiffEvent: CalendarEvent = null;
    let fullComposeDiffEvent: CalendarEvent = null;
    if (openedForm === 'FullCompose' && fullComposeEntrySource === 'QuickCompose') {
        const quickComposeFinalEvent = metricsInfo.quickComposeFinalEvent;
        quickComposeDiffEvent = generateEventDiff(quickComposeFinalEvent, originalEvent);
        fullComposeDiffEvent = generateEventDiff(event, quickComposeFinalEvent);
    }

    const serviceEvent = mapClientEventToServiceEvent(event, originalEvent);
    const viewState: FullComposeViewState = getFullComposeViewState();

    /** To attach new calendar event creation in the
     *  existing conversation thread, set InReplyTo to the
     *  message id. This is only needed for full compose scenario.
     *  Need more plumbing done for quick compose.
     */
    if (viewState?.internetMessageId) {
        diffEvent.InReplyTo = viewState.internetMessageId;
    }

    if (viewState?.attachmentWell) {
        diffEvent.DocLinks = serviceEvent.DocLinks = getDocLinks(
            viewState.attachmentWell.sharingLinkIds
        );
    }

    // FTDF flow modifies the CollabSpace before opening compose so we won't be able to
    // capture the diff in generateEventDiff.
    if (
        isFTDFSeriesMaster(event) &&
        isFeatureEnabled('cal-cmp-fluidCollaborativeSpace') &&
        !diffEvent.CollabSpace
    ) {
        diffEvent.CollabSpace = event.CollabSpace;
    }

    let promise: Promise<any> = null;

    logComposeFormOpenedTimes();
    logEventMetricProperties(
        perfDatapoint,
        diffEvent,
        quickComposeDiffEvent,
        fullComposeDiffEvent,
        event,
        EVENT_COMPLETION_STATUS_SAVED,
        openedForm,
        uiState.viewType,
        quickComposeEntrySource,
        metricsInfo,
        fullComposeEntrySource,
        originalEvent
    );

    if (isFeatureEnabled('cal-mf-sendrewardsfortimesuggestions')) {
        sendReinforcementLearningRewardsForTimeSuggestions(openedForm, event.Start, metricsInfo);
    }

    // We need to pass Start/End to the server when IsAllDay is changed or true
    if (originalEvent.IsAllDayEvent != serviceEvent.IsAllDayEvent || serviceEvent.IsAllDayEvent) {
        diffEvent.Start = serviceEvent.Start;
        diffEvent.End = serviceEvent.End;
    }

    if (viewState?.addin?.appendOnSend) {
        serviceEvent.AppendOnSend = viewState.addin.appendOnSend;
    }

    if (!isConsumer()) {
        // Log freebusy info when it's not a consumer scenario
        logFreeBusyInfo(perfDatapoint, event);
    }

    logSendActionMailTipsDatapoint(getMailTipsViewStateMap(event));

    let saveRequestTime: OwaDate = owaDate();
    setCurrentSaveRequestTimeStamp(saveRequestTime);

    if (!event.ItemId /* isNewEvent */) {
        promise = new Promise<CalendarEvent>((resolve, reject) =>
            newCalendarEvent(
                serviceEvent,
                uiState.viewType === 'TimePanel' ? 'TimePanelCompose' : 'ReadForm',
                resolve,
                reject
            )
        );
    } else {
        // We only update the calendar if there are changes
        let hasUpdates: boolean = Object.keys(diffEvent).length > 0;
        if (hasUpdates) {
            // if this update involves changing the account of the event, then trigger a delete and create instead of update (move)
            if (
                diffEvent.ParentFolderId &&
                event.ParentFolderId.mailboxInfo.userIdentity !=
                    originalEvent.ParentFolderId.mailboxInfo.userIdentity
            ) {
                const deletePromise = new Promise<void>(resolve =>
                    deleteCalendarEvent(
                        originalEvent,
                        uiState.viewType === 'TimePanel' ? 'TimePanelCompose' : 'EditForm',
                        originalEvent.CalendarItemType == 'ThisAndFollowingInstancesMaster'
                            ? EventScope.ThisAndFollowingInstances
                            : EventScope.Default,
                        resolve
                    )
                );

                const createPromise = new Promise((resolve, reject) =>
                    newCalendarEvent(
                        serviceEvent,
                        uiState.viewType === 'TimePanel' ? 'TimePanelCompose' : 'ReadForm',
                        resolve,
                        reject
                    )
                );

                // if originalEvent is a draft event (it is a New Event scenario with Attachments), hence in this case we want to create only upon successful delete of the draft event.
                // Else, if this an edit of existing event, we want to delete only upon successful create of the new event in the target account to avoid data loss.
                if (originalEvent.IsDraft) {
                    promise = new Promise<void>(async (resolve, reject) => {
                        try {
                            await deletePromise;
                            await createPromise;
                            resolve();
                        } catch {
                            reject();
                        }
                    });
                } else {
                    promise = new Promise<void>(async (resolve, reject) => {
                        let createResult = await createPromise;
                        if (createResult) {
                            await deletePromise;
                            resolve();
                        } else {
                            reject();
                        }
                    });
                }
            } else {
                // If item type is 'ThisAndFollowingInstancesMaster' we are editing a special hybrid of instance and series
                // therefore we need to pass FTDF scope into the update command.
                // Otherwise we pass EventScope.Default and let the server figure out based on ItemId
                // and type
                promise = new Promise<void>((resolve, reject) =>
                    updateCalendarEvent(
                        event,
                        diffEvent,
                        uiState.viewType === 'TimePanel' ? 'TimePanelCompose' : 'EditForm',
                        event.CalendarItemType == 'ThisAndFollowingInstancesMaster'
                            ? EventScope.ThisAndFollowingInstances
                            : EventScope.Default,
                        resolve,
                        reject,
                        true /* showNotification */
                    )
                );
            }
        } else {
            promise = new Promise(resolve => resolve(null));
        }
    }

    if (shouldCloseComposeForm()) {
        if (isInPopOutWindow()) {
            // We need to wait for saving finish in popout window, otherwise window will be closed
            // before the request is really sent out
            setAwaitingServiceResponse(true);

            if (openedForm === 'FullCompose' && fullComposeEntrySource === 'FindTime_VotingPage') {
                // Send a message to the findTimeVotingPage so it knows that the event was successfully created or not
                promise = promise
                    .then(createdItem => {
                        if (window.opener) {
                            // If we are hitting updateCalendarEvent instead of createCalendarEvent, we don't get the updated item back
                            // use event instead
                            var scheduledEvent = createdItem ?? event;
                            postMessage(window.opener, PopoutMessageType.SetData, {
                                EventId: (scheduledEvent as CalendarEvent).ItemId.Id,
                                StartTime: getISOString((scheduledEvent as CalendarEvent).Start),
                            });
                        }
                    })
                    .catch(e => {
                        if (window.opener) {
                            postMessage(window.opener, PopoutMessageType.SetData, { FTError: e });
                        }
                        throw e;
                    });
            }

            await promise
                .then(() => {
                    perfDatapoint.end();
                })
                .catch(e => {
                    perfDatapoint.endWithError(DatapointStatus.ClientError, e);
                });
        }

        // Close the form only in non-draft scenario
        closeComposeForm(true /* shouldResetComposeStore */, event.IsMeeting);
    }

    if (diffEvent.IsOnlineMeeting && !isInPopOutWindow()) {
        lazyShowOnlineMeetingFREDialog.importAndExecute();
    }

    if (
        !isInPopOutWindow() &&
        ((diffEvent.Start &&
            differenceInMinutes(diffEvent.Start, startOfHour(diffEvent.Start)) % 15 !== 0) ||
            (diffEvent.End &&
                differenceInMinutes(diffEvent.End, startOfHour(diffEvent.End)) % 15 !== 0))
    ) {
        lazyShowSpeedyMeetingsFREDialog.importAndExecute();
    }

    return promise
        .then(createdItem => {
            const { uiState } = getComposeStore();

            // Check if this response handler is not invalid
            // It is invalid if the store does not have view state (compose form closed) or
            // if the current saveRequestTime does not match the uiState.currentSaveRequestTimeStamp (newer request sent to the server, so no need to handle older request)
            const isThisValidResponseHandler =
                uiState && saveRequestTime == uiState.currentSaveRequestTimeStamp;

            if (isThisValidResponseHandler && createdItem) {
                // if this was a new event, update the event in the store with the createdItem
                updateToUserFriendlyEndDateForAllDay(createdItem);
                updateCalendarEvents(createdItem);

                onDraftEventCreated((createdItem as CalendarEvent).ItemId);
            }
            perfDatapoint.end();

            if (isFeatureEnabled('cal-mf-mobility')) {
                lazySaveTravelBookends.importAndExecute(
                    createdItem as CalendarEvent,
                    'CalendarCompose_Save'
                );
            }

            return createdItem;
        })
        .catch(e => perfDatapoint.endWithError(DatapointStatus.ClientError, e));
}

function shouldCloseComposeForm(): boolean {
    const { event } = getComposeStore();
    return !event.IsDraft;
}

function isSending(): boolean {
    const { event } = getComposeStore();
    return isEventAMeeting(event) && !event.IsDraft;
}

function updateToServiceFriendlyDatesForAllDay(event: CalendarEvent) {
    if (event.IsAllDayEvent) {
        event.Start = startOfDay(event.Start);
        event.End = startOfDay(addDays(event.End, 1));
    }
}

function updateToUserFriendlyEndDateForAllDay(event: CalendarEvent) {
    if (event.IsAllDayEvent) {
        event.End = addDays(event.End, -1);
    }
}

function mapClientEventToServiceEvent(event: CalendarEvent, originalEvent: CalendarEvent) {
    let serviceEvent: CalendarEvent = {} as CalendarEvent;

    // 1. For all day events, convert the EndDate to service type
    if (event.IsAllDayEvent) {
        // Create a copy as we are changing the start/end dates to make them server-friendly
        // but do not want to mutate the store instance
        serviceEvent = toJS(event);
        updateToServiceFriendlyDatesForAllDay(serviceEvent);
    } else {
        serviceEvent = event;
    }

    // 2. For any new inbox reminders, wipe out the client generated Id
    const originalInboxReminderIds = originalEvent.InboxReminders.map(reminder => reminder.Id);

    // serviceEvent.InboxReminders can be undefined if the data is overwritten by server data
    const curInboxReminderIds = serviceEvent.InboxReminders
        ? serviceEvent.InboxReminders.map(reminder => reminder.Id)
        : [];
    const newReminderIds = curInboxReminderIds.filter(
        reminderId => !originalInboxReminderIds.includes(reminderId)
    );
    convertToServiceInboxReminders(newReminderIds, serviceEvent.InboxReminders);

    return serviceEvent;
}
